/**
 * PWAUpdateBanner — surfaces a "Nouvelle version disponible" banner as soon
 * as a fresh service worker is `installed & waiting`.
 *
 * Why this exists: the PWA aggressively caches the app shell so returning
 * users used to hit `ERR_CACHE_MISS` or stale JWT tokens after a deploy
 * until they manually cleared their cache. The banner + one-click reload
 * closes that loop — see `/app/frontend/src/index.js` for the SW glue
 * that raises the `dm:sw-waiting` event we listen to here.
 */

import { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';

export default function PWAUpdateBanner() {
  const [waiting, setWaiting] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    const onWaiting = () => {
      setWaiting(true);
      // A fresh version wins over any previous dismiss — the user should
      // always see the prompt for a *new* deploy.
      setDismissed(false);
    };
    window.addEventListener('dm:sw-waiting', onWaiting);
    if (typeof window !== 'undefined' && window.__DM_WAITING_WORKER__) {
      setWaiting(true);
    }
    return () => window.removeEventListener('dm:sw-waiting', onWaiting);
  }, []);

  const handleReload = () => {
    setReloading(true);
    const worker = typeof window !== 'undefined' ? window.__DM_WAITING_WORKER__ : null;
    if (worker && typeof worker.postMessage === 'function') {
      // The SW replies to SKIP_WAITING → `activate` runs → the
      // `controllerchange` listener in index.js reloads the page.
      worker.postMessage({ type: 'SKIP_WAITING' });
      // Safety net: if the controllerchange event never fires (rare),
      // force a reload after 3s so the user isn't stuck.
      setTimeout(() => window.location.reload(), 3000);
    } else {
      window.location.reload();
    }
  };

  if (!waiting || dismissed) return null;

  return (
    <div
      data-testid="pwa-update-banner"
      role="status"
      aria-live="polite"
      className="fixed left-1/2 -translate-x-1/2 bottom-4 md:bottom-6 z-[9999] w-[calc(100%-1.5rem)] max-w-md rounded-xl shadow-2xl border border-sky-400/40 bg-slate-900/95 backdrop-blur text-white px-4 py-3 flex items-center gap-3"
    >
      <RefreshCw
        className={`h-5 w-5 text-sky-400 shrink-0 ${reloading ? 'animate-spin' : ''}`}
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold leading-tight">
          Nouvelle version disponible
        </div>
        <div className="text-xs text-slate-300 leading-snug">
          Rechargez pour appliquer les dernières mises à jour.
        </div>
      </div>
      <button
        data-testid="pwa-update-reload-btn"
        onClick={handleReload}
        disabled={reloading}
        className="text-sm font-medium rounded-md bg-sky-500 hover:bg-sky-400 disabled:opacity-60 disabled:cursor-wait text-white px-3 py-1.5 transition whitespace-nowrap"
      >
        {reloading ? 'Recharge…' : 'Recharger'}
      </button>
      <button
        data-testid="pwa-update-dismiss-btn"
        aria-label="Ignorer la mise à jour"
        onClick={() => setDismissed(true)}
        className="text-slate-400 hover:text-white p-1 rounded transition"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
